/**
 * Template for the Massachusetts Confidential Juror Questionnaire.
 *
 * Calibrated against tools/jurorquestionaire.jpeg (the blank PART 1/2/3 form).
 * Only the boxes defined here are ever read — nothing else on the page is
 * touched — which is how "extract only a certain amount of data" is enforced.
 *
 * PRIVACY: the form asks for Name, City/State/Zip, Place of Birth,
 * Race/Ethnicity, current Employer name, and Spouse name. NONE of those are
 * captured here — they are deliberately omitted so the pixels are never even
 * cropped, let alone recognized. This mirrors redactRawText() in core/privacy.ts
 * (name / DOB / address / employer / spouse-name are blocked). We keep only the
 * juror-selection-relevant, non-identifying fields. See memory: handwriting-form-ocr.
 *
 * ⚠️ COORDINATES ARE A FIRST-PASS CALIBRATION measured from a phone photo of the
 * blank form. The parser (form-parser.ts) applies these normalized boxes to the
 * raw image with NO deskew/crop, so for production accuracy re-measure against a
 * FLAT, page-fills-the-frame scan using tools/calibrate.html (drag a box per
 * field) and paste the generated `fields: [...]` back here. Keep the FIELDS list
 * in tools/calibrate.html in sync with the schema below.
 *
 * box = { x: left/pageW, y: top/pageH, w: width/pageW, h: height/pageH } (0..1).
 */
import type { FormTemplate } from '@/types/form';

/** US Letter portrait (8.5 x 11) reference aspect ratio. */
const LETTER_PORTRAIT = 8.5 / 11;

export const JURY_FORM_TEMPLATE: FormTemplate = {
  id: 'ma-jury-questionnaire-v2',
  aspectRatio: LETTER_PORTRAIT,
  fields: [
    // ── PART 1 — Your background information ──────────────────────────────
    // Sex / gender identity — text
    { id: 'sex_gender_identity', label: 'Sex / gender identity', type: 'text', box: { x: 0.78, y: 0.142, w: 0.2, h: 0.028 } },

    // Age — number
    { id: 'age', label: 'Age', type: 'number', box: { x: 0.92, y: 0.176, w: 0.07, h: 0.028 }, min: 18, max: 120 },

    // Qualified to serve (citizen, understands English, 18+)? — Yes / No
    {
      id: 'qualified',
      label: 'Qualified to serve (citizen, understands English, 18+)?',
      type: 'checkbox',
      markThreshold: 0.12,
      options: [
        { value: 'yes', box: { x: 0.8, y: 0.203, w: 0.02, h: 0.018 } },
        { value: 'no', box: { x: 0.88, y: 0.203, w: 0.02, h: 0.018 } }
      ]
    },

    // Education — highest grade completed — text
    { id: 'education_highest_grade', label: 'Education — highest grade completed', type: 'text', box: { x: 0.47, y: 0.226, w: 0.51, h: 0.028 } },

    // Current or past work / school — check all that apply
    {
      id: 'work_school_current_past',
      label: 'Current or past work / school (check all that apply)',
      type: 'checkbox',
      markThreshold: 0.12,
      options: [
        { value: 'employed', box: { x: 0.1, y: 0.282, w: 0.022, h: 0.018 } },
        { value: 'self_employed', box: { x: 0.1, y: 0.301, w: 0.022, h: 0.018 } },
        { value: 'at_home', box: { x: 0.1, y: 0.32, w: 0.022, h: 0.018 } },
        { value: 'student', box: { x: 0.1, y: 0.339, w: 0.022, h: 0.018 } },
        { value: 'unemployed', box: { x: 0.235, y: 0.282, w: 0.022, h: 0.018 } },
        { value: 'part_time', box: { x: 0.235, y: 0.301, w: 0.022, h: 0.018 } },
        { value: 'retired', box: { x: 0.235, y: 0.32, w: 0.022, h: 0.018 } },
        { value: 'other', box: { x: 0.235, y: 0.339, w: 0.022, h: 0.018 } }
      ]
    },

    // Type of business — text (industry, NOT employer name)
    { id: 'type_of_business', label: 'Type of business', type: 'text', box: { x: 0.6, y: 0.305, w: 0.2, h: 0.024 } },

    // Job / occupation — text
    { id: 'job_occupation', label: 'Job / occupation', type: 'text', box: { x: 0.56, y: 0.335, w: 0.42, h: 0.024 } },

    // Household — your status — check one
    {
      id: 'household_status',
      label: 'Household — your status',
      type: 'checkbox',
      markThreshold: 0.12,
      options: [
        { value: 'single', box: { x: 0.295, y: 0.37, w: 0.022, h: 0.018 } },
        { value: 'married', box: { x: 0.405, y: 0.37, w: 0.022, h: 0.018 } },
        { value: 'domestic_partner', box: { x: 0.505, y: 0.37, w: 0.022, h: 0.018 } },
        { value: 'separated', box: { x: 0.655, y: 0.37, w: 0.022, h: 0.018 } },
        { value: 'divorced', box: { x: 0.775, y: 0.37, w: 0.022, h: 0.018 } },
        { value: 'widowed', box: { x: 0.875, y: 0.37, w: 0.022, h: 0.018 } }
      ]
    },

    // Parent / guardian of any adult or minor children — No / Yes
    {
      id: 'parent_guardian_of_children',
      label: 'Parent / guardian of children',
      type: 'checkbox',
      markThreshold: 0.12,
      options: [
        { value: 'no', box: { x: 0.41, y: 0.403, w: 0.022, h: 0.018 } },
        { value: 'yes', box: { x: 0.47, y: 0.403, w: 0.022, h: 0.018 } }
      ]
    },

    // Age(s) of children — text (ages only; never names)
    { id: 'children_ages', label: 'Age(s) of children', type: 'text', box: { x: 0.61, y: 0.398, w: 0.27, h: 0.026 } },

    // ── PART 2 — Your spouse or partner (name & employer intentionally omitted) ──
    // Age of spouse / partner — number
    { id: 'partner_age', label: 'Age of spouse / partner', type: 'number', box: { x: 0.92, y: 0.468, w: 0.07, h: 0.028 }, min: 0, max: 120 },

    // Education of spouse / partner — text
    { id: 'spouse_partner_education', label: 'Education of spouse / partner', type: 'text', box: { x: 0.55, y: 0.494, w: 0.43, h: 0.026 } },

    // Spouse / partner current or past work / school — check all that apply
    {
      id: 'spouse_work_school',
      label: 'Spouse / partner current or past work / school (check all that apply)',
      type: 'checkbox',
      markThreshold: 0.12,
      options: [
        { value: 'employed', box: { x: 0.1, y: 0.545, w: 0.022, h: 0.018 } },
        { value: 'self_employed', box: { x: 0.1, y: 0.564, w: 0.022, h: 0.018 } },
        { value: 'at_home', box: { x: 0.1, y: 0.583, w: 0.022, h: 0.018 } },
        { value: 'student', box: { x: 0.1, y: 0.602, w: 0.022, h: 0.018 } },
        { value: 'unemployed', box: { x: 0.235, y: 0.545, w: 0.022, h: 0.018 } },
        { value: 'part_time', box: { x: 0.235, y: 0.564, w: 0.022, h: 0.018 } },
        { value: 'retired', box: { x: 0.235, y: 0.583, w: 0.022, h: 0.018 } },
        { value: 'other', box: { x: 0.235, y: 0.602, w: 0.022, h: 0.018 } }
      ]
    },

    // Type of business of spouse / partner — text (industry, NOT employer name)
    { id: 'spouse_type_of_business', label: 'Spouse / partner type of business', type: 'text', box: { x: 0.54, y: 0.565, w: 0.22, h: 0.024 } },

    // Spouse / partner's occupation — text
    { id: 'spouse_partner_occupation', label: "Spouse / partner's occupation", type: 'text', box: { x: 0.5, y: 0.598, w: 0.48, h: 0.024 } },

    // ── PART 3 — Your experience with the law ────────────────────────────
    // Gate: "Have you or anyone in your household or family ever had any of the
    // following experiences with the law?" — No / Yes (precedes the item list).
    {
      id: 'experience_with_law_gate',
      label: 'Any experience with the law (you or household/family)?',
      type: 'checkbox',
      markThreshold: 0.12,
      options: [
        { value: 'no', box: { x: 0.8, y: 0.663, w: 0.02, h: 0.018 } },
        { value: 'yes', box: { x: 0.9, y: 0.663, w: 0.02, h: 0.018 } }
      ]
    },

    // Experiences — check all that apply
    {
      id: 'experience_with_law',
      label: 'Experience with the law (check all that apply)',
      type: 'checkbox',
      markThreshold: 0.12,
      options: [
        { value: 'been_arrested', box: { x: 0.105, y: 0.684, w: 0.02, h: 0.016 } },
        { value: 'been_charged', box: { x: 0.315, y: 0.684, w: 0.02, h: 0.016 } },
        { value: 'been_convicted', box: { x: 0.545, y: 0.684, w: 0.02, h: 0.016 } },
        { value: 'been_crime_victim', box: { x: 0.775, y: 0.684, w: 0.02, h: 0.016 } },
        { value: 'been_sued', box: { x: 0.105, y: 0.702, w: 0.02, h: 0.016 } },
        { value: 'filed_lawsuit', box: { x: 0.315, y: 0.702, w: 0.02, h: 0.016 } },
        { value: 'been_witness', box: { x: 0.545, y: 0.702, w: 0.02, h: 0.016 } },
        { value: 'been_seated_juror', box: { x: 0.775, y: 0.702, w: 0.02, h: 0.016 } },
        { value: 'served_court_order', box: { x: 0.105, y: 0.72, w: 0.02, h: 0.016 } },
        { value: 'sought_court_order', box: { x: 0.315, y: 0.72, w: 0.02, h: 0.016 } }
      ]
    },

    // If yes, please describe — text
    { id: 'experience_with_law_describe', label: 'If yes, please describe', type: 'text', box: { x: 0.29, y: 0.738, w: 0.69, h: 0.028 } },

    // Gate: "Have you or anyone in your household or family ever worked for any
    // of the following?" — No / Yes (precedes the law-employment item list).
    {
      id: 'law_related_employment_gate',
      label: 'Anyone (you or household/family) worked in law/justice?',
      type: 'checkbox',
      markThreshold: 0.12,
      options: [
        { value: 'no', box: { x: 0.8, y: 0.773, w: 0.02, h: 0.018 } },
        { value: 'yes', box: { x: 0.9, y: 0.773, w: 0.02, h: 0.018 } }
      ]
    },

    // Law-related employment — check all that apply
    {
      id: 'law_related_employment',
      label: 'Law-related employment',
      type: 'checkbox',
      markThreshold: 0.12,
      options: [
        { value: 'law_enforcement', box: { x: 0.105, y: 0.794, w: 0.02, h: 0.016 } },
        { value: 'court_system', box: { x: 0.545, y: 0.794, w: 0.02, h: 0.016 } },
        { value: 'corrections', box: { x: 0.105, y: 0.812, w: 0.02, h: 0.016 } },
        { value: 'other_law_related', box: { x: 0.545, y: 0.812, w: 0.02, h: 0.016 } }
      ]
    },

    // If yes, please describe the law-related employment — text
    { id: 'law_related_employment_describe', label: 'Law-related employment — describe', type: 'text', box: { x: 0.29, y: 0.828, w: 0.69, h: 0.026 } },

    // Impartiality — "anything that might affect your ability to be fair?" No / Yes
    {
      id: 'impartiality',
      label: 'Impartiality — anything that might affect fairness?',
      type: 'checkbox',
      markThreshold: 0.12,
      options: [
        { value: 'no', box: { x: 0.77, y: 0.843, w: 0.022, h: 0.018 } },
        { value: 'yes', box: { x: 0.87, y: 0.843, w: 0.022, h: 0.018 } }
      ]
    },

    // If yes, describe what might affect impartiality — text
    { id: 'impartiality_describe', label: 'Impartiality — describe', type: 'text', box: { x: 0.29, y: 0.862, w: 0.69, h: 0.026 } }
  ]
};

export default JURY_FORM_TEMPLATE;
